import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Fade,
  Divider,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Container,
  Button,
  Drawer,
} from '@mui/material';
import {
  Gavel,
  Description,
  Search,
  ExitToApp,
  Home,
  Security,
  Menu as MenuIcon,
  Close as CloseIcon,
  AutoFixHigh,
  Article,
} from '@mui/icons-material';
import { AdminPanelSettings } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { CrisisDetector } from './CrisisDetection';
import Header from './Header';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { 
      text: 'Domů', 
      icon: <Home />, 
      path: '/',
      description: 'Hlavní stránka'
    },
    { 
      text: 'Blog', 
      icon: <Article />, 
      path: '/blog',
      description: 'Právní články a novinky'
    },
    { 
      text: 'Právní konzultace', 
      icon: <Gavel />, 
      path: '/dashboard?open=consultation',
      description: 'AI asistent pro právní dotazy'
    },
    { 
      text: 'Generování dokumentů', 
      icon: <Description />, 
      path: '/dashboard?open=documents',
      description: 'Tvorba právních dokumentů'
    },
    { 
      text: 'Analýza smluv', 
      icon: <Security />, 
      path: '/dashboard?open=contract-analysis',
      description: 'AI analýza rizik a doporučení'
    },
    { 
      text: 'Právní výzkum', 
      icon: <Search />, 
      path: '/dashboard?open=research',
      description: 'Vyhledávání v zákonech'
    },
    { 
      text: 'Admin', 
      icon: <AdminPanelSettings />, 
      path: '/admin',
      description: 'Správa a přehled uživatelů',
      admin: true,
    },
  ];

  const isActiveRoute = (path: string) => {
    // Normalize dashboard open links to their actual route for highlighting
    let normalized = path;
    if (path.startsWith('/dashboard?open=')) {
      const open = path.split('=')[1];
      const map: Record<string, string> = {
        consultation: '/consultation',
        documents: '/documents',
        'contract-analysis': '/contract-analysis',
        research: '/research'
      };
      normalized = map[open] || '/dashboard';
    }
    if (normalized === '/' && location.pathname === '/') return true;
    if (normalized !== '/' && location.pathname.startsWith(normalized)) return true;
    return false;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh',
      background: '#F2F2F2'
    }}>
      {/* Header - pouze pro homepage a blog */}
      {(location.pathname === '/' || 
        location.pathname.startsWith('/blog') ||
        location.pathname.endsWith('-info') ||
        location.pathname === '/pricing') && 
       <Header />}
      
      {/* Original Top Navigation Bar - Hidden */}
      <AppBar
        position="sticky" 
        elevation={0}
        sx={{
          display: 'none'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ py: 1 }}>
            {/* Logo */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                mr: 4
              }}
              onClick={() => navigate('/')}
            >
              <Avatar sx={{
                width: 40,
                height: 40,
                mr: 2,
                background: '#f5f5f5',
                '& svg': {
                  color: '#FF4B4B'
                }
              }}>
                <Security />
              </Avatar>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#212121',
                    lineHeight: 1.2,
                    fontSize: '1.1rem'
                  }}
                >
                  Právní asistent AI
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => {
                      // Preserve intended destination for post-auth redirect
                      try { sessionStorage.setItem('intendedPath', item.path); } catch {}
                      if (isAuthenticated) {
                        navigate(item.path);
                      } else {
                        const url = `/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(item.path)}`;
                        navigate(url);
                      }
                    }}
                    startIcon={React.cloneElement(item.icon, { 
                      sx: { 
                        fontSize: 20, 
                        color: (item as any).admin 
                          ? (isActiveRoute(item.path) ? '#b91c1c' : '#dc2626')
                          : (isActiveRoute(item.path) ? '#212121' : '#757575') 
                      } 
                    })}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 0,
                      fontWeight: (item as any).admin ? 700 : (isActiveRoute(item.path) ? 600 : 400),
                      fontSize: '0.95rem',
                      color: (item as any).admin 
                        ? (isActiveRoute(item.path) ? '#b91c1c' : '#dc2626')
                        : (isActiveRoute(item.path) ? '#212121' : '#757575'),
                      background: (item as any).admin ? 'rgba(220,38,38,0.08)' : 'transparent',
                      borderBottom: (item as any).admin 
                        ? (isActiveRoute(item.path) ? '2px solid #b91c1c' : '2px solid rgba(220,38,38,0.3)')
                        : (isActiveRoute(item.path) ? '2px solid #212121' : '2px solid transparent'),
                      transition: 'all 0.2s',
                      textTransform: 'none',
                      '&:hover': {
                        background: (item as any).admin ? 'rgba(220,38,38,0.12)' : 'transparent',
                        color: (item as any).admin ? '#b91c1c' : '#212121',
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ flexGrow: 1 }} />
            )}

            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {user && (
                <>
                  <Chip
                    avatar={
                      <Avatar sx={{ 
                        bgcolor: 'var(--liquid-gray-200)',
                        color: '#424242',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>
                        {user?.username?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </Avatar>
                    }
                    label={user?.username || user?.email || 'User'}
                    sx={{
                      background: 'rgba(255,255,255,0.6)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      color: '#424242',
                      fontWeight: 500,
                      display: { xs: 'none', sm: 'flex' }
                    }}
                  />
                  <IconButton
                    aria-label="Odhlásit se"
                    onClick={handleLogout}
                    sx={{
                      color: 'var(--liquid-gray-600)',
                      background: 'rgba(255,255,255,0.5)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.8)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <ExitToApp />
                  </IconButton>
                </>
              )}
              
              {isMobile && (
                <IconButton
                  aria-label="Otevřít menu"
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    color: '#424242',
                    background: 'rgba(255,255,255,0.5)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.8)',
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '85%', sm: 320 },
              maxWidth: '400px',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)',
              backdropFilter: { xs: 'blur(12px)', sm: 'blur(24px)' },
              WebkitBackdropFilter: { xs: 'blur(12px)', sm: 'blur(24px)' },
              borderLeft: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--liquid-gray-800)' }}>
                Menu
              </Typography>
              <IconButton
                onClick={() => setMobileOpen(false)}
                sx={{ color: 'var(--liquid-gray-600)' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <List>
              {menuItems.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    selected={isActiveRoute(item.path)}
                    sx={{
                      borderRadius: 3,
                      '&.Mui-selected': {
                        background: (item as any).admin ? 'rgba(220,38,38,0.12)' : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        border: (item as any).admin ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(255,255,255,0.4)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)',
                      },
                      '&:hover': {
                        background: (item as any).admin ? 'rgba(220,38,38,0.12)' : 'rgba(255,255,255,0.6)',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: (item as any).admin ? '#dc2626' : '#424242' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      secondary={item.description}
                      primaryTypographyProps={{
                        fontWeight: (item as any).admin ? 700 : (isActiveRoute(item.path) ? 600 : 500),
                        color: (item as any).admin ? '#b91c1c' : '#212121'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: (item as any).admin ? '#ef4444' : 'var(--liquid-gray-600)'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      )}

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          position: 'relative',
          width: '100%',
          minHeight: '100vh',
          background: '#F2F2F2'
        }}
        id="main-content"
        tabIndex={-1}
      >
        <Outlet />
      </Box>


      {/* Crisis Detector */}
      <CrisisDetector />
    </Box>
  );
};

export default Layout;
